import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminCampaigns from '../components/AdminCampaigns';
import AdminRoles from '../components/AdminRoles';
import AdminNewsletter from '../components/AdminNewsletter';
import AdminBanners from '../components/AdminBanners';
import AdminPopups from '../components/AdminPopups';
import AdminAnnouncements from '../components/AdminAnnouncements';
import supabase from '../supabase';
import { theme, fontImport } from '../components/adminTheme';
import AdminNotify from '../components/AdminNotify';
import AdminNavbar, { SIDEBAR_WIDTH, BREAKPOINT } from '../components/AdminNavbar';
import LoadingScreen from '../components/LoadingScreen';
import AdminSeal from '../components/AdminSeal';
import AdminLoginBg from '../components/AdminLoginBg';
import AdminCampaignVideos from '../components/AdminCampaignVideos';
import AdminVideoTeaser from '../components/AdminVideoTeaser';
import AdminPostcards from '../components/AdminPostcards';
import AdminPostcardSends from '../components/AdminPostcardSends';
import { AdminLangProvider, useAdminLang } from '../components/AdminLangContext';

const SECTIONS = {
    fr: [
        {
            label: 'Campagnes',
            items: [
                { id: 'campaigns', label: 'Campagnes' },
                { id: 'campaignvideos', label: 'Vidéos' },
                { id: 'videoteaser', label: 'Icône teaser' },
            ],
        },
        {
            label: 'Communication',
            items: [
                { id: 'newsletter', label: 'Newsletter' },
                { id: 'notify', label: 'Notifications' },
                { id: 'banners', label: 'Bandeaux' },
                { id: 'popups', label: 'Pop-ups' },
                { id: 'announcements', label: 'Communiqués' },
            ],
        },
        {
            label: 'Apparence',
            items: [
                { id: 'seal', label: 'Sceau' },
                { id: 'loginbg', label: 'Login BG' },
            ],
        },
        {
            label: 'Cartes postales',
            items: [
                { id: 'postcards', label: 'Albums' },
                { id: 'postcardrequests', label: 'Envois' },
            ],
        },
        {
            label: 'Accès',
            items: [
                { id: 'roles', label: 'Rôles' },
            ],
        },
    ],
    en: [
        {
            label: 'Campaigns',
            items: [
                { id: 'campaigns', label: 'Campaigns' },
                { id: 'campaignvideos', label: 'Videos' },
                { id: 'videoteaser', label: 'Teaser icon' },
            ],
        },
        {
            label: 'Communication',
            items: [
                { id: 'newsletter', label: 'Newsletter' },
                { id: 'notify', label: 'Notifications' },
                { id: 'banners', label: 'Banners' },
                { id: 'popups', label: 'Pop-ups' },
                { id: 'announcements', label: 'Announcements' },
            ],
        },
        {
            label: 'Appearance',
            items: [
                { id: 'seal', label: 'Seal' },
                { id: 'loginbg', label: 'Login BG' },
            ],
        },
        {
            label: 'Postcards',
            items: [
                { id: 'postcards', label: 'Albums' },
                { id: 'postcardrequests', label: 'Sends' },
            ],
        },
        {
            label: 'Access',
            items: [
                { id: 'roles', label: 'Roles' },
            ],
        },
    ],
};

const T = {
    fr: { eyebrow: 'Gestion de la plateforme' },
    en: { eyebrow: 'Platform management' },
};

function AdminManageInner() {
    const navigate = useNavigate();
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const sections = SECTIONS[lang] || SECTIONS.fr;
    const [activeTab, setActiveTab] = useState('campaigns');
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => { document.title = 'Management — winston.ie Studio'; }, []);

    useEffect(() => {
        const checkAccess = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) { navigate('/admin/login'); return; }

            const { data: userRow, error } = await supabase
                .from('users').select('role').eq('user_id', session.user.id).single();

            if (!error && userRow?.role?.toLowerCase() === 'admin') {
                setIsAdmin(true);
            } else {
                navigate('/admin/login');
            }
            setCheckingAccess(false);
        };
        checkAccess();
    }, [navigate]);

    if (checkingAccess || !isAdmin) {
        return <LoadingScreen variant="light" />;
    }

    const activeLabel = sections.flatMap(s => s.items).find(i => i.id === activeTab)?.label || '';

    return (
        <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text }}>
            <style>{fontImport}</style>
            <style>{`
                .admin-manage-content {
                    margin-left: ${SIDEBAR_WIDTH}px;
                    max-width: 1096px;
                    padding: 48px 40px 80px;
                    box-sizing: border-box;
                }
                @media (max-width: ${BREAKPOINT}px) {
                    .admin-manage-content {
                        margin-left: 0;
                        padding: 88px 24px 60px;
                    }
                }
            `}</style>
            <AdminNavbar sections={sections} activeTab={activeTab} onSelect={setActiveTab} />

            <div className="admin-manage-content">
                <p style={{
                    fontFamily: "'Helvetica Neue', Arial, sans-serif", fontWeight: 700, fontSize: '10px',
                    letterSpacing: '0.22em', textTransform: 'uppercase', color: theme.textMuted, margin: '0 0 10px',
                }}>
                    {t.eyebrow}
                </p>
                <h1 style={{
                    fontFamily: "'Helvetica Neue', Arial, sans-serif", fontWeight: 700,
                    fontSize: 'clamp(24px, 3vw, 32px)', letterSpacing: '-0.01em', color: theme.text,
                    margin: '0 0 40px',
                }}>
                    {activeLabel}
                </h1>

                {activeTab === 'campaigns' && <AdminCampaigns />}
                {activeTab === 'newsletter' && <AdminNewsletter />}
                {activeTab === 'banners' && <AdminBanners />}
                {activeTab === 'popups' && <AdminPopups />}
                {activeTab === 'announcements' && <AdminAnnouncements />}
                {activeTab === 'roles' && <AdminRoles />}
                {activeTab === 'notify' && <AdminNotify />}
                {activeTab === 'seal' && <AdminSeal />}
                {activeTab === 'loginbg' && <AdminLoginBg />}
                {activeTab === 'campaignvideos' && <AdminCampaignVideos />}
                {activeTab === 'videoteaser' && <AdminVideoTeaser />}
                {activeTab === 'postcards' && <AdminPostcards />}
                {activeTab === 'postcardrequests' && <AdminPostcardSends />}

            </div>
        </div>
    );
}

function AdminManage() {
    return (
        <AdminLangProvider>
            <AdminManageInner />
        </AdminLangProvider>
    );
}

export default AdminManage;